import { nanoid } from 'nanoid';
import { recommendationEventsApi } from '@/api/qwReplyRecommend';
import { shouldRetryEventBatch } from './eventQueueCore';

const LEGACY_STORAGE_KEYS = ['qw-reply-recommend-events-v2'];
const STORAGE_KEY = 'qw-reply-recommend-events-v3';
const MAX_QUEUE = 300;
const MAX_EVENT_AGE_MS = 30 * 60 * 1000;

// 队列持久化到 localStorage，页面刷新或短暂断网后仍可用原 eventId 重试，保持服务端幂等。
/** 从本地存储恢复待上报事件，损坏数据按空队列处理。 */
function eventTimestamp(item) {
  const value = item && item.event ? item.event.eventTime : '';
  const timestamp = Date.parse(String(value || '').replace(' ', 'T'));
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function isFreshEvent(item) {
  const timestamp = eventTimestamp(item);
  return timestamp > 0 && Date.now() - timestamp <= MAX_EVENT_AGE_MS;
}

function cleanupLegacyQueues() {
  LEGACY_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
}

function load() {
  try {
    cleanupLegacyQueues();
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.filter(isFreshEvent) : [];
  } catch (_) { return []; }
}
/** 持久化最近 MAX_QUEUE 条事件，限制本地存储占用。 */
function save(queue) { localStorage.setItem(STORAGE_KEY, JSON.stringify(queue.filter(isFreshEvent).slice(-MAX_QUEUE))); }
/** 生成与后端约定一致的本地事件时间文本。 */
function eventTime() {
  const date = new Date();
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** 创建按 sessionToken 分批、失败保留原 eventId 重试的埋点队列。 */
export function createRecommendationEventQueue() {
  let queue = load();
  let flushing = false;
  let timer = null;
  /** 从队列中移除服务端已处理或已明确拒绝的一批事件。 */
  function removeBatch(batch) {
    const ids = new Set(batch.map(item => item.event.eventId));
    queue = queue.filter(item => !ids.has(item.event.eventId));
    save(queue);
  }
  /** 发送队首令牌对应的一批事件，成功后再从本地队列移除。 */
  async function flush() {
    queue = queue.filter(isFreshEvent);
    save(queue);
    if (flushing || !queue.length) return;
    flushing = true;
    try {
      // sessionToken 绑定一个企微页面会话，批量接口不能混合不同 token，故每次只发送队首同组事件。
      const token = queue[0].sessionToken;
      const batch = queue.filter(item => item.sessionToken === token).slice(0, 50);
      await recommendationEventsApi({ sessionToken: token, events: batch.map(item => item.event) });
      removeBatch(batch);
      if (queue.length) schedule(100);
    } catch (error) {
      if (shouldRetryEventBatch(error)) {
        // 网络错误和 5xx 保留原 eventId 延迟重试，避免一次交互被统计多次。
        schedule(3000);
      } else {
        // 4xx、令牌过期和业务参数错误不会自行恢复，移除该批以免阻塞后续页面会话。
        removeBatch(batch);
        console.warn('[qwReplyRecommend] discard non-retryable event batch', error);
        if (queue.length) schedule(100);
      }
    }
    finally { flushing = false; }
  }
  /** 合并短时间内的刷新请求，只保留一个定时器。 */
  function schedule(delay = 300) { if (timer) clearTimeout(timer); timer = setTimeout(flush, delay); }
  return {
    /** 为事件补充稳定 ID 与时间并持久化等待上报。 */
    enqueue(sessionToken, event) {
      if (!sessionToken) return;
      queue.push({ sessionToken, event: Object.assign({ eventId: nanoid(), eventTime: eventTime() }, event) });
      save(queue); schedule();
    },
    flush,
    updateSessionToken(oldToken, newToken) {
      if (!oldToken || !newToken || oldToken === newToken) return;
      queue = queue.map(item => item.sessionToken === oldToken ? Object.assign({}, item, { sessionToken: newToken }) : item);
      save(queue);
    },
    /** 清理队列调度定时器；已持久化事件保留供下次页面重试。 */
    destroy() { if (timer) clearTimeout(timer); timer = null; },
  };
}
